import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle2, ChevronRight, Sparkles, FileDown, ArrowRight,
  Target, BarChart3, Building, Compass, Sliders, Layers, AlertTriangle,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";

// Phase 25.13ak — AU Mid-Tier ADI AI Maturity Benchmark landing page.
//
// Public, no-auth lead-magnet page that surfaces the externally-shareable
// benchmark PDF (see scripts/generate_ai_maturity_benchmark.py) and
// captures requests from ADI execs who want their bank's section refreshed
// with non-public context or included in the next quarterly cycle.
//
// Designed as the conversion target for the Steve-Kinsella-style "soft-
// touch" DM #1 in the BNK outreach play: bankers can land here without
// signing in, grab the PDF, and submit a request without committing to a
// procurement conversation.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIMENSIONS = [
  { Icon: Layers, name: "D1 · Data Infrastructure", pitch: "Single-source-of-truth customer + transaction warehouse. Real-time event stream. APIs internal-facing first." },
  { Icon: Sliders, name: "D2 · Customer-Facing AI", pitch: "Product features ML-personalised (not rules-based). In-app financial-wellbeing nudges. AI-grounded customer support." },
  { Icon: BarChart3, name: "D3 · Internal Operations AI", pitch: "Underwriting + collections + KYC + reconciliations running ML-assisted. Measurable FTE redeployment." },
  { Icon: AlertTriangle, name: "D4 · Risk & Compliance AI", pitch: "AML + fraud + market-conduct surveillance ML-driven. CPS 230 + CPS 234 evidence trail automated." },
  { Icon: Compass, name: "D5 · Ecosystem / Partnerships", pitch: "Open Banking + CDR consumer + business productive. Live fintech partnerships. Embedded-finance API monetised." },
];

const COVERED_ADIS = [
  { name: "Judo Bank", size: "~$10b · SME-focused" },
  { name: "Suncorp Bank", size: "~$60b · ANZ subsidiary" },
  { name: "Tyro Payments", size: "Merchant acquirer · $50b TPV" },
  { name: "MyState Bank", size: "~$8b · TAS HQ" },
  { name: "Auswide Bank", size: "~$5b · QLD regional" },
  { name: "BNK Banking", size: "~$2b · BNK + Better Choice" },
];

const FINDINGS = [
  { num: "01", title: "Average maturity = 11 / 25.", body: "No mid-tier in this cohort scores above 13. There is no first-mover yet. 18-month window before that crystallises." },
  { num: "02", title: "Data (D1) is the consistent weak link.", body: "Average 2.5 / 5. Building customer-AI on a 2.5 data-infra score is why most programs stall in Year 2." },
  { num: "03", title: "Ecosystem (D5) is the asymmetric upside.", body: "Average 1.8 / 5. Mid-tiers that ship Open-Banking-grade APIs in 2026 capture aggregator-data flywheel ahead of the Big Four." },
  { num: "04", title: "2 of 6 in CEO transition.", body: "BNK (interim, post-Allan Savins) + Suncorp (post-ANZ integration). Transition periods correlate with paused strategic AI spend." },
];

const FAQ = [
  { q: "Will my institution see the benchmark?", a: "Yes — distribution is free. We share the PDF directly with you and circulate it through industry channels. We do not paywall it." },
  { q: "Can I correct the score?", a: "Yes. Use the form on this page. Refreshes happen quarterly. We'll incorporate non-public context under NDA on request." },
  { q: "Is this a vendor pitch in disguise?", a: "Partly honest: we publish this benchmark because mid-tier ADIs are increasingly asking us what we can do as a partner. The benchmark is genuinely independent. Page 4 of the PDF explains how DARA can plug into each phase — that's the only paid-product section." },
  { q: "What if my bank is not in this cohort?", a: "Submit the form and let us know which ADI you'd like included. We refresh quarterly and prioritise based on requests." },
  { q: "Who scored this?", a: "DARA OS's research team. Sources: FY24/FY25 annual reports, AGM transcripts, APRA stat returns, public product launches, LinkedIn job postings (ML/data hires per 1k FTE), fintech-partnership announcements." },
];

export default function AIBenchmark() {
  usePageview("/ai-maturity-benchmark");

  const [form, setForm] = useState({
    email: "", name: "", company: "", role: "", interest: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.company) {
      toast.error("Work email and institution are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: form.company,
        role: form.role,
        message: [
          form.interest ? `Interest: ${form.interest}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "ai-maturity-benchmark",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Request received — DARA will follow up within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const benchmarkUrl = `${API}/public/ai-maturity-benchmark`;
  const memoUrl = `${API}/public/bank-partnership-memo`;
  const onePagerUrl = `${API}/public/overview-one-pager`;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ai-benchmark-page">
      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 hidden sm:inline">
              · AI MATURITY BENCHMARK
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <a
              href={benchmarkUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-download-benchmark"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] flex items-center gap-1.5"
            >
              <FileDown className="w-3.5 h-3.5" /> Download PDF
            </a>
            <a
              href="#request"
              data-testid="nav-request"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90"
            >
              Request your section
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFB000]/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // EXTERNAL BENCHMARK · INDUSTRY READ · UPDATED FEB 2026 · Q2 REFRESH MAY 2026
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">
            AU Mid-Tier ADIs. <span className="text-[#FFB000]">AI Maturity, scored.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-300 mt-6 max-w-3xl leading-relaxed">
            A free 4-page external read by DARA OS scoring six representative AU mid-tier authorised deposit-taking institutions on five sequencing-aware AI dimensions. Built for boards operating on a $2-8M annual AI envelope. Refreshed quarterly. No paid placements. No vendor lock-in.
            <span className="text-white font-bold"> Free PDF. No email required.</span>
          </p>

          <div className="flex flex-wrap gap-2 mt-7" data-testid="hero-meta-pills">
            {["JUDO", "SUNCORP", "TYRO", "MYSTATE", "AUSWIDE", "BNK", "5 DIMENSIONS", "QUARTERLY"].map(p => (
              <span key={p} className="px-2.5 py-1 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 font-mono text-[10px] uppercase tracking-widest">{p}</span>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href={benchmarkUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-download-benchmark"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <FileDown className="w-4 h-4" /> Download the benchmark <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#request"
              data-testid="hero-request-section"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5"
            >
              <Target className="w-3.5 h-3.5" /> Request your bank's section
            </a>
          </div>
        </div>
      </section>

      {/* Dimensions */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// HOW WE SCORED</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Five sequencing-aware dimensions. <span className="text-zinc-500">0-5 each. 25 total.</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-4 max-w-3xl leading-relaxed">
          Scoring rubric: 5 = leading practice in 2026 globally; 3 = on-trend with the AU Big-Four median; 1 = active program but limited visible output; 0 = no program. Total maps to one of three buckets: <span className="text-[#1B7F40]">Leading (≥17)</span> · <span className="text-[#FFB000]">Emerging (12-16)</span> · <span className="text-[#FF6B47]">Foundational (≤11)</span>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 mt-10 border border-zinc-900" data-testid="dimensions-grid">
          {DIMENSIONS.map(({ Icon, name, pitch }) => (
            <div key={name} className="bg-black p-6">
              <Icon className="w-5 h-5 text-[#FFB000] mb-3" />
              <div className="font-heading font-bold text-base tracking-tight">{name}</div>
              <p className="text-xs text-zinc-400 leading-relaxed mt-2">{pitch}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Covered ADIs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// WHO'S IN THE COHORT</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Six AU mid-tier ADIs. <span className="text-[#FFB000]">More each quarter — on request.</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-zinc-900 mt-10 border border-zinc-900" data-testid="adi-grid">
          {COVERED_ADIS.map(({ name, size }) => (
            <div key={name} className="bg-black p-5">
              <Building className="w-4 h-4 text-[#FFB000] mb-2" />
              <div className="font-heading font-bold text-sm tracking-tight">{name}</div>
              <p className="text-[11px] text-zinc-500 mt-1 leading-snug">{size}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Findings */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// HEADLINE FINDINGS · MID-TIER ADIs · 2026</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          The pattern across the cohort. <span className="text-[#FFB000]">Why most programs stall in Year 2.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="findings-grid">
          {FINDINGS.map(({ num, title, body }) => (
            <div key={num} className="bg-black p-6 sm:p-7">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// FINDING {num}</div>
              <h3 className="font-heading font-bold text-lg tracking-tight">{title}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <a
            href={benchmarkUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="findings-download-benchmark"
            className="inline-flex items-center gap-2 border border-[#FFB000]/40 hover:bg-[#FFB000]/10 text-[#FFB000] font-mono text-[11px] uppercase tracking-widest px-4 py-2.5"
          >
            <FileDown className="w-3.5 h-3.5" /> Full 4-page benchmark PDF
          </a>
          <a
            href={memoUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="findings-download-memo"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000]"
          >
            <FileDown className="w-3.5 h-3.5" /> Bank Partnership Memo
          </a>
          <a
            href={onePagerUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="findings-download-onepager"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000]"
          >
            <FileDown className="w-3.5 h-3.5" /> DARA 1-Pager
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// QUESTIONS WE GET</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Honest answers.
        </h2>
        <div className="mt-10 border border-zinc-900 divide-y divide-zinc-900" data-testid="faq-list">
          {FAQ.map(({ q, a }) => (
            <details key={q} className="group">
              <summary className="cursor-pointer px-5 py-4 flex items-center justify-between font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:bg-zinc-950/50">
                <span>{q}</span>
                <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90 text-zinc-500" />
              </summary>
              <div className="px-5 pb-5 text-sm text-zinc-400 leading-relaxed">{a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* Request form */}
      <section id="request" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="border border-[#FFB000]/40 bg-[#FFB000]/[0.03] p-6 sm:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">// REQUEST YOUR BANK'S SECTION</div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
            Want your institution refreshed with non-public context? <span className="text-[#FFB000]">No procurement. No deck.</span>
          </h2>
          <p className="text-sm text-zinc-300 mt-3 max-w-3xl leading-relaxed">
            Submit your details. DARA will reach out within one business day. You can: <b>(a)</b> review your current score privately before next refresh, <b>(b)</b> share non-public context under NDA, <b>(c)</b> have your ADI included in the next quarter's cohort, or <b>(d)</b> simply book a 30-min conversation. Pick zero or more.
          </p>

          {!submitted && (
            <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="benchmark-request-form">
              <Input label="Work email *" testid="benchmark-email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Input label="Full name" testid="benchmark-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Input label="Institution *" testid="benchmark-company" required value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
              <Input label="Role / title" testid="benchmark-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">What's the interest?</span>
                  <select
                    value={form.interest}
                    onChange={(e) => setForm({ ...form, interest: e.target.value })}
                    data-testid="benchmark-interest"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  >
                    <option value="">— Pick one (optional) —</option>
                    <option value="review-our-score">Review our current score privately</option>
                    <option value="share-context">Share non-public context (under NDA)</option>
                    <option value="include-our-adi">Include our ADI in next quarter's cohort</option>
                    <option value="30-min-conversation">Just a 30-min conversation</option>
                    <option value="partnership-models">Discuss partnership models</option>
                    <option value="other">Other</option>
                  </select>
                </label>
              </div>
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Anything you want to flag (optional)</span>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="benchmark-message"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  />
                </label>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-3 pt-2">
                <p className="font-mono text-[10px] text-zinc-600 max-w-md">
                  By submitting you accept that DARA will contact you about this benchmark. We never share or sell your details. Mutual NDA available on request.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="benchmark-submit"
                  className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-40"
                >
                  {submitting ? <><Sparkles className="w-3.5 h-3.5 animate-pulse" /> SUBMITTING…</> : <><ChevronRight className="w-3.5 h-3.5" /> Submit request</>}
                </button>
              </div>
            </form>
          )}

          {submitted && (
            <div className="mt-8 border border-[#00E676]/40 bg-[#00E676]/5 p-6" data-testid="benchmark-success">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#00E676] mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" />
                REQUEST RECEIVED
              </div>
              <h3 className="font-heading font-bold text-2xl tracking-tight">You're on the list.</h3>
              <p className="text-sm text-zinc-300 mt-2 max-w-xl leading-relaxed">
                DARA will reach out within one business day.
                In the meantime, <a href={benchmarkUrl} target="_blank" rel="noopener noreferrer" className="text-[#FFB000] underline">download the 4-page benchmark PDF</a> to circulate internally.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Assets</div>
            <ul className="space-y-2">
              <li><a href={benchmarkUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">AI Maturity Benchmark PDF</a></li>
              <li><a href={memoUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Bank Partnership Memo</a></li>
              <li><a href={onePagerUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">DARA 1-Pager</a></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Product</div>
            <ul className="space-y-2">
              <li><Link to="/founders-guide" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Founder's Guide</Link></li>
              <li><Link to="/security" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Security & Trust</Link></li>
              <li><Link to="/pricing" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Pricing</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Account</div>
            <ul className="space-y-2">
              <li><Link to="/login" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Sign in</Link></li>
              <li><Link to="/signup" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Create workspace</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">DARA OS</div>
            <p className="text-[11px] text-zinc-600 leading-relaxed">
              The Bloomberg-terminal for enterprise operators. Built in Australia, deployed worldwide.
            </p>
            <p className="text-[10px] text-zinc-700 leading-relaxed mt-3 font-mono">
              Corrections: benchmark@daraos.ai
            </p>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-4 sm:px-6 py-4 max-w-7xl mx-auto flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">© DARA OS · {new Date().getFullYear()}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-700">BENCHMARK · v1 · FEB 2026</span>
        </div>
      </footer>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}
